// The client-side rules for custom field values, shared by the record page (CustomFieldValueInput) and
// the Opportunities list's inline editors, so the two surfaces cannot accept different
// input. custom-field-service's validateCustomFieldValues is still the final authority; a value it
// rejects is a failed save, not something these rules have to anticipate.
import moment from 'moment';
import {profileLocales} from '../../../constants';

export const isValidInteger = (val: string | null): boolean => {
	if (val === null || val === '') return true;
	return /^-?\d+$/.test(val);
};

export const integerConverterValue = (value: string | number | null) => {
	if (value === null || value === undefined || value === '') {
		return {float: null, full: '', error: false};
	}
	const str = String(value).trim();
	if (/^-?\d+$/.test(str)) {
		return {float: parseInt(str, 10), full: str, error: false};
	}
	return {float: null, full: str, error: false};
};

export const filterIntegerInput = (val: string): string => {
	const stripped = val.replace(/[^0-9-]/g, '');
	if (stripped === '-') return '-';
	const negative = stripped.startsWith('-');
	const digits = stripped.replace(/-/g, '');
	return (negative ? '-' : '') + digits;
};

// The decimal separator a viewer types and reads, from their profile language. Unknown or missing
// languages use '.'.
export const decimalSeparatorFor = (language: string | null | undefined): ',' | '.' =>
	profileLocales[language as keyof typeof profileLocales]?.usesCommaDecimal ? ',' : '.';

// Stored decimals always use '.'; a user may type the locale separator.
export const normaliseDecimal = (val: string): string => val.replace(',', '.');

// Accepts both '.' and ',' as decimal separators (normalises before testing)
export const isValidDecimal = (val: string | null): boolean => {
	if (val === null || val === '') return true;
	return /^-?\d+(\.\d+)?$/.test(normaliseDecimal(String(val)));
};

// Returns a converterValue function that accepts the locale decimal separator.
// Stored values always arrive with '.'; user-typed values use decSep.
export const makeDecimalConverter = () => (value: string | number | null) => {
	if (value === null || value === undefined || value === '') {
		return {float: null, full: '', error: false};
	}
	const str = String(value).trim();
	const normalized = normaliseDecimal(str);
	if (/^-?\d+(\.\d+)?$/.test(normalized)) {
		return {float: parseFloat(normalized), full: str, error: false};
	}
	return {float: null, full: str, error: true};
};

// Returns a filterInput function that only allows the locale decimal separator.
export const makeDecimalFilter =
	(decSep: string) =>
	(val: string): string => {
		const allowPattern = decSep === ',' ? /[^0-9,-]/g : /[^0-9.-]/g;
		const dupPattern = decSep === ',' ? /,/g : /\./g;
		let result = val.replace(allowPattern, '');
		const isNegative = result.startsWith('-');
		result = result.replace(/-/g, '');
		if (isNegative) result = '-' + result;
		const sepIdx = result.indexOf(decSep);
		if (sepIdx !== -1) {
			result = result.slice(0, sepIdx + 1) + result.slice(sepIdx + 1).replace(dupPattern, '');
		}
		return result;
	};

// The service stores ISO dates only; the record page parses strictly with the same format.
export const isValidIsoDate = (val: string | null): boolean => {
	if (val === null || val === '') return true;
	return moment(val, 'YYYY-MM-DD', true).isValid();
};

// A dropdown value is stored as the option's numeric id, while GraphQL hands the option out with its
// global id ("CustomFieldDropdownOption:<id>", base64). Same decode the record page's ComboBox uses.
export const dropdownOptionKey = (optionGlobalId: string): string => atob(optionGlobalId).split(':')[1];
